import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import CustomText from "../atomic/CustomText";
import Button from "../organism/Button";

export type DocumentIntelligenceProcessingStep =
  | "uploading"
  | "analyzing"
  | "extracting";

const GSAP_PRIMARY = "#18B5B7";
const GSAP_BASE = "#ffffff";

export interface DocumentIntelligenceProcessingModalProps {
  visible: boolean;
  embedded?: boolean;
  step: DocumentIntelligenceProcessingStep;
  currentIndex: number;
  total: number;
  progressPercent: number;
  error: string | null;
  inProgress: boolean;
  onClose: () => void;
  onRetry?: () => void;
  onCancel?: () => void;
}

function DocumentIntelligenceProcessingModal({
  visible,
  embedded = false,
  step,
  progressPercent,
  error,
  inProgress,
  onClose,
  onRetry,
  onCancel,
  currentIndex,
  total,
}: Readonly<DocumentIntelligenceProcessingModalProps>): JSX.Element | null {
  const loaderRootRef = useRef<HTMLDivElement>(null);
  const shapeOneRef = useRef<HTMLDivElement>(null);
  const shapeTwoRef = useRef<HTMLDivElement>(null);
  const shapeThreeRef = useRef<HTMLDivElement>(null);

  const stepTitles: Record<DocumentIntelligenceProcessingStep, string> = {
    uploading: "Uploading",
    analyzing: "Analyzing",
    extracting: "Preparing",
  };
  const processingCopy = [
    "Analyzing your document...",
    "Understanding the content...",
    "Extracting important information...",
    "Preparing the results...",
  ] as const;
  const [copyIndex, setCopyIndex] = useState(0);
  const clampedPercent = Math.min(100, Math.max(0, progressPercent));

  useEffect(() => {
    if (!visible || error || !inProgress) return;
    if (step === "uploading") {
      setCopyIndex(0);
      return;
    }
    if (step === "extracting") {
      setCopyIndex(3);
      return;
    }

    setCopyIndex(1);
    const timeoutId = globalThis.setTimeout(() => {
      setCopyIndex(2);
    }, 3000);
    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [visible, error, inProgress, step]);

  useEffect(() => {
    if (!visible) return;
    if (
      !loaderRootRef.current ||
      !shapeOneRef.current ||
      !shapeTwoRef.current ||
      !shapeThreeRef.current
    )
      return;

    const reducedMotion =
      globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ??
      false;

    const context = gsap.context(() => {
      gsap.set(shapeOneRef.current, { scale: 1, backgroundColor: GSAP_BASE });
      gsap.set(shapeTwoRef.current, { scale: 0.72, rotation: 45 });
      gsap.set(shapeThreeRef.current, { scale: 0.84, borderRadius: "30%" });

      if (reducedMotion) return;

      gsap.to(shapeOneRef.current, {
        scale: 1.08,
        duration: 1.25,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(shapeOneRef.current, {
        backgroundColor: GSAP_PRIMARY,
        duration: 4.5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(shapeTwoRef.current, {
        rotation: "+=360",
        duration: 1.6,
        repeat: -1,
        ease: "none",
      });

      gsap.to(shapeTwoRef.current, {
        scale: 0.9,
        duration: 1.3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(shapeThreeRef.current, {
        rotation: "+=720",
        duration: 2.8,
        repeat: -1,
        ease: "none",
      });

      gsap.to(shapeThreeRef.current, {
        scale: 0.94,
        duration: 1.4,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(shapeThreeRef.current, {
        borderRadius: "100%",
        duration: 2.2,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, loaderRootRef);

    return () => context.revert();
  }, [visible, embedded]);

  const loaderVisual = (
    <div className="w-full mb-6 flex items-center justify-center" aria-hidden>
      <div ref={loaderRootRef} className="relative w-[120px] h-[120px]">
        <div
          ref={shapeOneRef}
          className="absolute inset-0 rounded-full bg-white"
        />
        <div
          ref={shapeTwoRef}
          className="absolute inset-0 rounded-full bg-sideNavGray"
        />
        <div
          ref={shapeThreeRef}
          className="absolute inset-0 bg-sideNavGray"
        />
      </div>
    </div>
  );

  if (!visible) return null;

  if (embedded) {
    return (
      <div className="bg-white rounded-lg p-5 max-w-md w-full min-h-[200px] border border-borderGray overflow-hidden shadow-sm flex flex-col justify-between">
        {loaderVisual}
        {error ? (
          <>
            <CustomText
              text={error}
              fontWeight="font-normal"
              fontSize="text-sm"
              className="text-requiredRed mb-4"
            />
            <div className="flex gap-3 justify-end">
              {onRetry && (
                <Button
                  buttonTheme="primary"
                  textInformation={{
                    text: "Retry",
                    fontWeight: "font-semibold",
                    fontSize: "text-sm",
                  }}
                  onClick={onRetry}
                  type="button"
                />
              )}
            </div>
          </>
        ) : (
          <div>
            <div>
              <CustomText
                text={stepTitles[step]}
                fontWeight="font-medium"
                className="reviewCardHeading text-royalBlue mb-2 text-center"
              />
            </div>
            <div>
              <CustomText
                text={processingCopy[copyIndex]}
                fontWeight="font-normal"
                fontSize="text-sm"
                className="text-darkGray mb-1 text-center"
              />
            </div>
            {/* <CustomText
              text={`${Math.round(clampedPercent)}% complete${total > 0 ? ` (${Math.min(currentIndex, total)}/${total})` : ""}`}
              fontWeight="font-normal"
              fontSize="text-xs"
              className="text-darkGray mb-3 text-center"
            /> */}
            {inProgress && onCancel && (
              <div className="mt-4 flex justify-end">
                <Button
                  buttonTheme="secondary"
                  textInformation={{
                    text: "Cancel",
                    fontWeight: "font-semibold",
                    fontSize: "text-sm",
                  }}
                  onClick={onCancel}
                  type="button"
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-modalBackdrop p-4">
      <div
        className="bg-white rounded-lg p-5 max-w-md w-full min-h-[200px] border border-borderGray overflow-hidden shadow-sm flex flex-col justify-between"
        role="dialog"
        aria-modal="true"
        aria-labelledby="processing-modal-title"
        aria-describedby="processing-modal-desc"
      >
        {loaderVisual}

        {error ? (
          <>
            <CustomText
              text={error}
              fontWeight="font-normal"
              fontSize="text-sm"
              className="text-requiredRed mb-4"
            />
            <div className="flex gap-3 justify-end">
              <Button
                buttonTheme="secondary"
                textInformation={{
                  text: "Close",
                  fontWeight: "font-semibold",
                  fontSize: "text-sm",
                }}
                onClick={onClose}
                type="button"
              />
              {onRetry && (
                <Button
                  buttonTheme="primary"
                  textInformation={{
                    text: "Retry",
                    fontWeight: "font-semibold",
                    fontSize: "text-sm",
                  }}
                  onClick={onRetry}
                  type="button"
                />
              )}
            </div>
          </>
        ) : (
          <div>
            <div id="processing-modal-title">
              <CustomText
                text={stepTitles[step]}
                fontWeight="font-medium"
                className="reviewCardHeading text-royalBlue mb-2 text-center"
              />
            </div>
            <div id="processing-modal-desc">
              <CustomText
                text={processingCopy[copyIndex]}
                fontWeight="font-normal"
                fontSize="text-sm"
                className="text-darkGray mb-1 text-center"
              />
            </div>
            {/* <CustomText
              text={`${Math.round(clampedPercent)}% complete${total > 0 ? ` (${Math.min(currentIndex, total)}/${total})` : ""}`}
              fontWeight="font-normal"
              fontSize="text-xs"
              className="text-darkGray mb-3 text-center"
            /> */}
            {inProgress && onCancel && (
              <div className="mt-4 flex justify-end">
                <Button
                  buttonTheme="secondary"
                  textInformation={{
                    text: "Cancel",
                    fontWeight: "font-semibold",
                    fontSize: "text-sm",
                  }}
                  onClick={onCancel}
                  type="button"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentIntelligenceProcessingModal;
